
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/database.types';
import { useToast } from '@/components/ui/use-toast';

export const useUserData = () => {
  const { toast } = useToast();
  
  const { 
    data: users, 
    isLoading, 
    isError, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      console.log('Fetching profiles from Supabase...');
      
      try {
        // In a production environment, we'd check if the user is an admin
        // But for demo purposes, we'll skip this check and allow access
        console.log('DEMO MODE: All users have admin access');
        
        // With our new RLS policy, this query should now return all profiles
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching profiles:', error);
          throw {
            ...error,
            message: error.message || 'Error fetching user profiles'
          };
        }
        
        console.log('Fetched profiles:', data?.length || 0);
        console.log('Profile data:', data);
        
        if (!data || data.length === 0) {
          console.warn('No profiles found in the database');
        }
        
        return data as Profile[];
      } catch (error: any) {
        // Show toast notification for errors
        toast({
          title: "Error loading users",
          description: error?.message || "An unknown error occurred",
          variant: "destructive",
        });
        throw error;
      }
    },
    retry: 1,
    staleTime: 60000 // 1 minute
  });

  // Calculate recent users
  const recentUsers = users?.filter(user => 
    new Date(user.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  ) || [];

  useEffect(() => {
    console.log('AdminUsers component mounted, fetching data');
    refetch();
  }, [refetch]);

  return {
    users,
    recentUsers,
    isLoading,
    isError,
    error,
    refetch
  };
};
